<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-text-field
      outlined
      label="Date d'accès *"
      v-model="access.dateIn"
      :rules="validation.dateIn"
      v-mask="['##.##.####']"
      hint="Format attendu JJ.MM.AAAA"
      dense
    />
    <v-text-field
      outlined
      label="Heure d'accès *"
      v-model="access.timeIn"
      v-mask="['##:##']"
      hint="Format attendu HH:MM"
      dense
    />
    <v-text-field type="number" outlined label="Durée en heures *" v-model="access.duration" dense />
    <v-text-field
      outlined
      label="Salle(s) qui sera(ont) utilisée(s) *"
      v-model="access.location"
      dense
      hint="Veuillez séparer les valeurs par des virgules"
      :persistent-hint="true"
    />
    <v-radio-group v-model="type" row>
      <v-radio label="Département" color="primary" value="departments"></v-radio>
      <v-radio label="Centre" color="primary" value="centers"></v-radio>
      <v-radio label="Service" color="primary" value="services"></v-radio>
      <v-radio label="Support transversal" color="primary" value="transversals"></v-radio>
    </v-radio-group>
    <v-select
      dense
      outlined
      :label="unities.labels[type]"
      v-model="access.unity"
      :items="unities[type]"
    ></v-select>
    <v-text-field
      outlined
      label="Personne qui effectue la demande *"
      v-model="access.requesterEmail"
      dense
      disabled
    />
    <v-autocomplete
      v-model="access.managerEmail"
      :items="managers"
      :search-input.sync="searchManagers"
      label="Directeur d’Institut ou Supérieur hiérarchique *"
      outlined
      dense
      @input="clearSearchManagers"
      no-data-text="Tapez le nom d'une personne..."
    />
    <v-autocomplete
      v-model="access.accessorsEmail"
      :items="accessors"
      :search-input.sync="searchAccessors"
      label="Personne(s) qui sera(ont) sur site *"
      outlined
      dense
      multiple
      @input="clearSearchAccessors"
      no-data-text="Tapez le nom d'une personne..."
    >
      <template v-slot:selection="data">
        <v-chip
          small
          v-bind="data.attrs"
          :input-value="data.selected"
          close
          @click="data.select"
          @click:close="remove(data.item)"
          class="mt-2"
        >{{data.item.text}}</v-chip>
      </template>
    </v-autocomplete>
    <v-textarea
      dense
      outlined
      label="Motif de la demande *"
      v-model="access.reason"
      rows="3"
      auto-grow
      required
    ></v-textarea>
    <v-snackbar v-model="snackbar.visible" :bottom="true" :left="true" :color="snackbar.color">
      {{snackbar.text}}
      <v-btn dark text @click="snackbar.visible = false">Fermer</v-btn>
    </v-snackbar>
  </v-form>
</template>

<script>
import axios from "axios";
import { mask } from "vue-the-mask";
import { parse } from "date-fns";
import _ from "lodash";

export default {
  name: "AccessControlForm",
  props: ["initialData"],
  directives: { mask },
  async mounted() {
    this.loading = true;
    this.access = this.initialData;
    this.loading = true;
    this.loadNames();
    this.formattedUnities = Object.keys(this.unities)
      .flatMap(key => {
        if (key !== "labels") {
          const formatted = this.unities[key].map(u => ({
            value: u,
            type: key
          }));
          return formatted;
        }
      })
      .filter(x => x);
    this.type = this.formattedUnities.find(
      fo => fo.value === this.access.unity
    ).type;
  },
  data: () => ({
    access: {
      dateIn: "",
      timeIn: "",
      duration: "",
      location: "",
      unity: "",
      requesterEmail: "",
      managerEmail: "",
      accessorsEmail: [],
      reason: ""
    },
    formattedUnities: [],
    unities: {
      labels: {
        departments: "Département *",
        centers: "Centre *",
        services: "Service *",
        transversals: "Support transversal *"
      },
      departments: ["COMEM+", "EC+G", "HEG", "TIC", "TIN"],
      centers: [
        "Formation continue",
        "Formation de base",
        "Ra&D",
        "Relations Internationales"
      ],
      services: [
        "Communication & vie du campus",
        "Finances",
        "Infrastructure & bâtiments",
        "Ressources humaines",
        "Informatique & systèmes d'information"
      ],
      transversals: [
        "Direction & Réception",
        "Développement & accréditation",
        "SIPA",
        "Secrétariat académique"
      ]
    },
    type: "departments",
    searchManagers: null,
    managers: [],
    searchAccessors: null,
    accessors: [],
    loading: false,
    submitting: false,
    snackbar: {
      visible: false,
      color: "",
      text: ""
    },
    valid: false,
    validation: {
      dateIn: [
        value => {
          if (value) {
            const parsed = parse(value, "dd.MM.yyyy", new Date());
            if (parsed.toString() !== "Invalid Date" && value.length === 10) {
              return true;
            }
            return "Format attendu JJ.MM.AAAA";
          }
          return true;
        }
      ]
    }
  }),
  watch: {
    searchManagers: _.debounce(function(val) {
      val &&
        val !== this.access.managerEmail &&
        this.queryPicker(val, "managers");
    }, 300),
    searchAccessors: _.debounce(function(val) {
      val &&
        val !== this.access.accessorsEmail &&
        this.queryPicker(val, "accessors");
    }, 300),
    "initialData._id": function(_id) {
      if (_id) {
        this.access = this.initialData;
        this.loadNames();
        this.type = this.formattedUnities.find(
          fo => fo.value === this.access.unity
        ).type;
      }
    }
  },
  methods: {
    async loadNames() {
      const { data: managers } = await axios({
        method: "post",
        url: `${process.env.VUE_APP_PEOPLEPICKER_URI}/users`,
        data: [this.access.managerEmail],
        headers: {
          "x-api-key": process.env.VUE_APP_PEOPLEPICKER_KEY
        }
      });
      const { data: accessors } = await axios({
        method: "post",
        url: `${process.env.VUE_APP_PEOPLEPICKER_URI}/users`,
        data: [...this.access.accessorsEmail],
        headers: {
          "x-api-key": process.env.VUE_APP_PEOPLEPICKER_KEY
        }
      });
      this.managers = managers;
      this.accessors = accessors;
    },
    async queryPicker(v, key) {
      this.loading = true;
      const { data: contacts } = await axios({
        method: "get",
        url: `${process.env.VUE_APP_PEOPLEPICKER_URI}/search?s=${v}`,
        headers: {
          "x-api-key": process.env.VUE_APP_PEOPLEPICKER_KEY
        }
      });
      contacts.map(c =>
        this[key].push({
          text: `${c.firstname} ${c.lastname}`,
          value: c.email
        })
      );
      this[key] = _.uniqBy(this[key], "value");
      this.loading = false;
    },
    clearSearchManagers() {
      this.searchManagers = null;
    },
    clearSearchAccessors() {
      this.searchAccessors = null;
    },
    remove(item) {
      const index = this.access.accessorsEmail.indexOf(item.value);
      if (index >= 0) this.access.accessorsEmail.splice(index, 1);
    }
  },
  computed: {
    requiredFields: function() {
      return (
        !!this.access.dateIn &&
        !!this.access.timeIn &&
        !!this.access.duration &&
        !!this.access.location &&
        !!this.access.unity &&
        !!this.access.requesterEmail &&
        !!this.access.managerEmail &&
        this.access.accessorsEmail.length &&
        !!this.access.reason
      );
    }
  }
};
</script>